import Modal from "react-bootstrap/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SetStatisticsModalIsShown } from "../../Store";
import { Button, ButtonGroup, Card, Col, Row, ToggleButton } from "react-bootstrap";
import { getFinishedAppsBetweenDates, getSentAppsBetweenDates } from "../../Store/Thunk";
import Form from "react-bootstrap/Form";
import ScrollableCard from "../../../../SharedComponents/ScrollableCard";
import FinishedApplicationsStatisticsList from "./FinishedApplicationsStatisticsList";
import SentApplicationsStatisticsList from "./SentApplicationsStatisticsList";

const StatisticsModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isShown = useSelector((state) => state.adminDash.statisticsModalIsShown);
  const finishedApplications = useSelector((state) => state.adminDash.finishedApplicationsByDaysCount);
  const sentApplications = useSelector((state) => state.adminDash.sentApplicationsByDaysCount);
  const windowSizeCssClass = useSelector((state) => state.appMenu.screenSize);

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // Add one day
    return tomorrow.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };

  const getThreeDaysBeforeToday = () => {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 3); // Move back 3 days
    return pastDate.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  };

  const [startDate, setStartDate] = useState(getThreeDaysBeforeToday());
  const [endDate, setEndDate] = useState(getTomorrowDate());
  const [isTypeFinished, setIsTypeFinished] = useState(false);

  useEffect(() => {
    if (startDate && endDate && isShown) {
      dispatch(getSentAppsBetweenDates({ startDate, endDate }));
      dispatch(getFinishedAppsBetweenDates({ startDate, endDate }));
    }
  }, [startDate, endDate, isShown, dispatch]);

  const bundledSentApplications = useMemo(() => {
    if (!sentApplications) return [];

    return sentApplications.reduce((acc, application) => {
      const date = application.sentDate;

      let dateGroup = acc.find((group) => group.date === date);
      if (!dateGroup) {
        dateGroup = { date, candidates: [] };
        acc.push(dateGroup);
      }

      let candidateGroup = dateGroup.candidates.find(
        (candidate) => candidate.candidateName === application.candidateName
      );
      if (!candidateGroup) {
        candidateGroup = {
          candidateName: application.candidateName,
          candidateId: application.candidateId,
          applications: [],
        };
        dateGroup.candidates.push(candidateGroup);
      }

      candidateGroup.applications.push(application.applicationName);

      return acc;
    }, []);
  }, [sentApplications]);

  return (
    <Modal
      size={"xl"}
      show={isShown}
      onHide={() => {
        dispatch(SetStatisticsModalIsShown(false));
      }}
      dialogClassName={`custom-modal-dialog ${windowSizeCssClass}`}
      contentClassName={"custom-modal-content"}
    >
      <div className={"bordered"}>
        <Modal.Header closeButton className={"p-0 border-bottom-0 mb-3"}>
          <Col>
            <Row className={"justify-content-between"}>
              <Col xs={"auto"} className={"align-content-center"}>
                <Row className={"mx-0"}>
                  <ButtonGroup className="mb-2">
                    <ToggleButton
                      id={"test-unarchive-button"}
                      type="radio"
                      className={`button align-content-center  text-nowrap ${!isTypeFinished ? "secondary bg-white" : "success primary"} `}
                      value={!isTypeFinished}
                      checked={!isTypeFinished}
                      onClick={() => setIsTypeFinished(true)}
                    >
                      {t("Finished")}
                    </ToggleButton>
                    <ToggleButton
                      id={"test-archive-button"}
                      className={`button align-content-center  text-nowrap ${isTypeFinished ? "secondary bg-white" : "success primary"} `}
                      type="radio"
                      value={isTypeFinished}
                      checked={isTypeFinished}
                      onClick={() => setIsTypeFinished(false)}
                    >
                      {t("Sent")}
                    </ToggleButton>
                  </ButtonGroup>
                </Row>
              </Col>
              <Col xs={"auto"} className={"align-content-center"}>
                <Form>
                  <Row>
                    <Col>
                      <Form.Label>{t("Start Date")}</Form.Label>
                      <Form.Control
                        type="date"
                        value={startDate}
                        max={endDate || getTomorrowDate()}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Form.Label>{t("End Date")}</Form.Label>
                      <Form.Control
                        type="date"
                        value={endDate}
                        min={startDate}
                        max={getTomorrowDate()}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Col>
        </Modal.Header>

        <Row className={"justify-content-between"}>
          <Col className={"align-content-center"}>
            <Card className={"default-list"} style={{ backgroundColor: "#f8f9fa" }}>
              <Card className={"header mb-0"} style={{ backgroundColor: "white" }}>
                <Row className={"text-20 text-color-gray-light fw-medium"} style={{ height: "50px", margin: "0" }}>
                  <Col xs={3} className={"align-content-center"}>
                    <Row className={"justify-content-center"}>{t("dateHeader")}</Row>
                  </Col>
                  <Col className={"align-content-center"}>
                    <Row>
                      <Col>
                        <Row className={"justify-content-center"}>{t("candidatesHeader")}</Row>
                      </Col>
                      <Col>
                        <Row className={"justify-content-center"}>{t("assessmentsHeader")}</Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>

              <ScrollableCard bottom={80}>
                {!isTypeFinished ? (
                  <SentApplicationsStatisticsList bundledSentApplications={bundledSentApplications} />
                ) : (
                  <FinishedApplicationsStatisticsList finishedApplications={finishedApplications} />
                )}
              </ScrollableCard>
            </Card>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default StatisticsModal;
